import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { Plus, X, Calendar, Filter, ListChecks, Pencil } from "lucide-react";
import { PATROL_STATUS_LABEL, PATROL_STATUS_COLOR, isPatrolOverdue, formatDateTimeAR, isoFromARDateTime } from "@/lib/i18n";
import { Time24Select } from "@/components/Time24Select"; // Fix AW
import { isOperationalRole, roleLabel } from "@/lib/roles";
import { SiteSelect } from "@/components/SiteSelect";
import { SiteAssignedHint } from "@/components/SiteAssignedHint";
import { ExportButtons } from "@/components/ExportButtons"; // Fix BS

const STATUS_BADGE = PATROL_STATUS_COLOR;

export default function AdminPatrols() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate(); // Fix AW
  const [list, setList] = useState(null);
  const [monthCounts, setMonthCounts] = useState(null); // Fix BH: totales reales de "No realizadas del mes"
  const [clients, setClients] = useState([]);
  const [vigiladores, setVigiladores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", site_id: "", vigilador_id: "", date: "", time: "", tolerance_minutes: 60 }); // Fix AW: fecha + hora 24 hs
  const [busy, setBusy] = useState(false);
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const [filters, setFilters] = useState({ status: searchParams.get("status") || "", vigilador_id: "", client_id: "", site_id: "", date: searchParams.get("date") || "", origin: "", unassigned: "" });

  const load = async () => {
    try {
      const params = [];
      if (filters.status === "vencida") params.push("status=pendiente");
      else if (filters.status === "vencida_mes") { // Fix BH: filtro del lado del servidor (antes se cortaba en 500)
        params.push("status=vencida_mes");
        if (filters.client_id) params.push(`client_id=${filters.client_id}`);
      }
      else if (filters.status) params.push(`status=${filters.status}`);
      if (filters.vigilador_id) params.push(`vigilador_id=${filters.vigilador_id}`);
      if (filters.origin) params.push(`origin=${filters.origin}`);
      if (filters.unassigned) params.push("unassigned=true");
      if (filters.status === "vencida_mes") {
        const now = new Date();
        const arNow = new Date(now.toLocaleString("en-US", { timeZone: "America/Argentina/Buenos_Aires" }));
        const y = arNow.getFullYear(), mo = arNow.getMonth();
        const monthStart = new Date(y, mo, 1).toISOString().slice(0, 10);
        const monthEnd = new Date(y, mo + 1, 1).toISOString().slice(0, 10);
        params.push(`date_from=${monthStart}T00:00:00`);
        params.push(`date_to=${monthEnd}T00:00:00`);
      } else if (filters.date) {
        params.push(`date_from=${filters.date}T00:00:00`);
        params.push(`date_to=${filters.date}T23:59:59`);
      }
      const [p, c, u] = await Promise.all([
        api.get(`/patrols${params.length ? `?${params.join("&")}` : ""}`),
        api.get("/clients"), api.get("/users/roster"),
      ]);
      let rows = p.data;
      if (filters.status === "vencida") rows = rows.filter(isPatrolOverdue);
      else if (filters.status === "vencida_mes") rows = rows.filter((r) => r.status === "no_realizada" || (r.status === "pendiente" && isPatrolOverdue(r)));
      if (filters.client_id) rows = rows.filter((r) => r.client_id === filters.client_id);
      if (filters.site_id) rows = rows.filter((r) => r.site_id === filters.site_id); // Fix AU
      if (filters.status === "vencida_mes") { // Fix BH
        const mc = await api.get(`/patrols/overdue-month-counts${filters.client_id ? `?client_id=${filters.client_id}` : ""}`);
        setMonthCounts(mc.data);
      } else setMonthCounts(null);
      setList(rows); setClients(c.data); setVigiladores((u.data || []).filter((x) => isOperationalRole(x.role) && x.active));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [filters]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const iso = isoFromARDateTime(form.date, form.time); // Fix AW: hora argentina, 24 hs
      await api.post("/patrols", { client_id: form.client_id, site_id: form.site_id || null, vigilador_id: form.vigilador_id, scheduled_for: iso, tolerance_minutes: parseInt(form.tolerance_minutes) });
      toast.success("Ronda creada");
      setOpen(false); setForm({ client_id: "", site_id: "", vigilador_id: "", date: "", time: "", tolerance_minutes: 60 });
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const cancel = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("¿Cancelar esta ronda? No se puede deshacer.")) return;
    try { await api.delete(`/patrols/${id}`); toast.success("Ronda cancelada"); load(); }
    catch (e2) { toast.error(formatApiError(e2)); }
  };

  // Fix BQ (2026-11-03): sacar rondas vencidas o no realizadas. No se borran: quedan "Cancelada" con
  // motivo y dejan de contar como no realizadas.
  const isDiscardable = (p) => p.status === "no_realizada" || (p.status === "pendiente" && isPatrolOverdue(p));
  const discard = async (ids) => {
    const reason = window.prompt(`Motivo para sacar ${ids.length === 1 ? "esta ronda" : `${ids.length} rondas`} (queda registrado):`);
    if (reason === null) return;
    if (reason.trim().length < 3) { toast.error("Indicá el motivo"); return; }
    try {
      const { data } = await api.post("/patrols/discard", { ids, reason: reason.trim() });
      toast.success(`${data.discarded} ronda(s) quitadas de no realizadas${data.skipped ? ` · ${data.skipped} salteadas` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const finalizeOverdue = async () => {
    if (!window.confirm("¿Regularizar el backlog de rondas pendientes vencidas hace más de 24 hs? Pasan a estado 'No realizada' y dejan de contar en la alerta del inicio (siguen consultables acá con el filtro).")) return;
    setFinalizeBusy(true);
    try {
      const { data } = await api.post("/patrol-schedules/finalize-overdue");
      toast.success(`${data.finalized} ronda(s) regularizada(s) a "No realizada".`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setFinalizeBusy(false); }
  };

  const counts = useMemo(() => {
    if (!list) return null;
    // Fix BH: con "No realizadas del mes" los contadores son los totales del servidor, no lo que entró en la lista.
    if (monthCounts) return { vencida: monthCounts.vencida, no_realizada: monthCounts.no_realizada };
    return list.reduce((acc, p) => {
      const key = isPatrolOverdue(p) ? "vencida" : p.status;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [list, monthCounts]);

  return (
    <div style={{ padding: "20px 24px 60px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Operación</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 0", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Rondas</h1>
          {counts && <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 11 }}>
            {Object.entries(counts).map(([k, v]) => <span key={k} className={`badge ${STATUS_BADGE[k] || "gray"}`}>{PATROL_STATUS_LABEL[k] || k}: {v}</span>)}
          </div>}
          {monthCounts && list && monthCounts.total > list.length && (
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }} data-testid="patrols-month-truncated">
              Total del mes: {monthCounts.total}. Se muestran las {list.length} más recientes.
            </div>
          )}
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="patrol-new"><Plus size={14} style={{ marginRight: 6 }}/>Nueva ronda</button>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button onClick={finalizeOverdue} disabled={finalizeBusy} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11 }} data-testid="patrol-finalize-overdue"><ListChecks size={13} style={{ marginRight: 6 }}/>{finalizeBusy ? "Regularizando..." : "Regularizar vencidas antiguas"}</button>
      </div>

      {/* Filters */}
      <div className="card" style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", padding: 14, marginBottom: 14 }}>
        <Filter size={16} style={{ color: "#BF3437", alignSelf: "center" }} />
        <div className="field" style={{ marginBottom: 0, flex: "1 1 140px" }}>
          <label>Estado</label>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
            <option value="">Todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="vencida">Vencida (no realizada, últ. 24hs)</option>
            <option value="vencida_mes">No realizadas del mes en curso</option>
            <option value="no_realizada">No realizada (histórico, +24hs)</option>
            <option value="en_curso">En curso</option>
            <option value="completada">Completada</option>
            <option value="incompleta">Incompleta</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 160px" }}>
          <label>Cliente</label>
          <select value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value, site_id: "" })} data-testid="filter-client">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {/* Fix AU (2026-09-29): filtro por sede (aparece si el objetivo tiene sedes). */}
        {filters.client_id && (
          <div style={{ flex: "1 1 160px" }}>
            <SiteSelect clientId={filters.client_id} value={filters.site_id} onChange={(v) => setFilters((f) => ({ ...f, site_id: v }))} testIdPrefix="filter" label="Sede" />
          </div>
        )}
        <div className="field" style={{ marginBottom: 0, flex: "1 1 160px" }}>
          <label>Vigilador</label>
          <select value={filters.vigilador_id} onChange={(e) => setFilters({ ...filters, vigilador_id: e.target.value })} data-testid="filter-vigilador">
            <option value="">Todos</option>
            {vigiladores.map((v) => <option key={v.id} value={v.id}>{v.name} · {roleLabel(v.role)}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 140px" }}>
          <label>Fecha</label>
          <input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} data-testid="filter-date"/>
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 140px" }}>
          <label>Origen</label>
          <select value={filters.origin} onChange={(e) => setFilters({ ...filters, origin: e.target.value })} data-testid="filter-origin">
            <option value="">Todas</option>
            <option value="auto">Automáticas</option>
            <option value="manual">Manuales</option>
          </select>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 2 }}>
          <input type="checkbox" checked={!!filters.unassigned} onChange={(e) => setFilters({ ...filters, unassigned: e.target.checked })} data-testid="filter-unassigned" /> Sin asignar
        </label>
        {(filters.status || filters.client_id || filters.site_id || filters.vigilador_id || filters.date || filters.origin || filters.unassigned) && (
          <button type="button" className="btn-ghost" onClick={() => setFilters({ status: "", vigilador_id: "", client_id: "", site_id: "", date: "", origin: "", unassigned: "" })} style={{ padding: "8px 12px", fontSize: 11 }}>Limpiar</button>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <ExportButtons kind="patrols" rows={list} testIdPrefix="patrols" filtersLabel={[
          filters.status && `Estado: ${filters.status === "vencida_mes" ? "No realizadas del mes" : filters.status === "vencida" ? "Vencidas" : (PATROL_STATUS_LABEL[filters.status] || filters.status)}`,
          filters.client_id && `Objetivo: ${clients.find((c) => c.id === filters.client_id)?.name || ""}`,
          filters.vigilador_id && `Vigilador: ${vigiladores.find((v) => v.id === filters.vigilador_id)?.name || ""}`,
          filters.date && `Fecha: ${filters.date.split("-").reverse().join("/")}`,
          filters.origin && `Origen: ${filters.origin === "auto" ? "Automáticas" : "Manuales"}`,
          filters.unassigned && "Sin asignar",
        ].filter(Boolean).join(" · ")} />
      </div>
      {list && list.filter(isDiscardable).length > 0 && (filters.status === "vencida" || filters.status === "vencida_mes" || filters.status === "no_realizada") && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button type="button" className="btn-ghost" onClick={() => discard(list.filter(isDiscardable).map((p) => p.id))} style={{ padding: "8px 12px", fontSize: 11, color: "#BF3437", borderColor: "#BF3437" }} data-testid="patrol-discard-listed">
            Sacar las {list.filter(isDiscardable).length} listadas de no realizadas
          </button>
        </div>
      )}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {list == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
        {list && list.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "var(--invict-text-muted)" }}>Sin rondas con esos filtros.</div>}
        {list && list.map((p) => {
          const overdue = isPatrolOverdue(p);
          return (
          <Link to={`/admin/rondas/${p.id}`} key={p.id} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 2fr 1fr 1fr auto", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--invict-bone-3)", alignItems: "center", color: "inherit", textDecoration: "none" }} data-testid={`patrol-row-${p.id}`}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#0A0A0A" }}>{p.client_name}{p.site_name ? <span style={{ color: "#BF3437" }}> · {p.site_name}</span> : null}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{p.site_address || p.client_address || ""}</div>
            </div>
            <div style={{ fontSize: 13 }}>
              {p.vigilador_name}
              {p.vigilador_deleted && <DeletedUserBadge id={p.id} />}
              {!p.vigilador_id && <span className="badge red" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`patrol-unassigned-${p.id}`}>SIN ASIGNAR</span>}
              {p.auto_generated && <span className="badge blue" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`patrol-auto-${p.id}`}>AUTOMÁTICA</span>}
            </div>
            <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>
              <Calendar size={11} style={{ display: "inline", marginRight: 4 }} />
              {formatDateTimeAR(p.scheduled_for)}
            </div>
            <div style={{ fontSize: 12 }}>{p.points_completed}/{p.total_points}{p.anomalies > 0 && <span style={{ color: "#BF3437", fontWeight: 700, marginLeft: 4 }}>· {p.anomalies}!</span>}</div>
            <div><span className={`badge ${overdue ? STATUS_BADGE.vencida : (STATUS_BADGE[p.status] || "gray")}`} data-testid={`patrol-status-${p.id}`}>{overdue ? PATROL_STATUS_LABEL.vencida : (PATROL_STATUS_LABEL[p.status] || p.status)}</span></div>
            <div style={{ display: "flex", gap: 6 }}>{p.status === "pendiente" && <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/admin/rondas/${p.id}?editar=1`); }} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10 }} title="Cambiar horario / reasignar" data-testid={`patrol-edit-${p.id}`}><Pencil size={11}/></button>}{p.status === "pendiente" && !overdue && <button onClick={(e) => cancel(p.id, e)} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`patrol-cancel-${p.id}`}><X size={11}/></button>}{isDiscardable(p) && <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); discard([p.id]); }} className="btn-ghost" style={{ padding: "5px 8px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} title="Sacar de no realizadas (queda cancelada con motivo)" data-testid={`patrol-discard-${p.id}`}><X size={11}/></button>}</div>
          </Link>
          );
        })}
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h3>Nueva ronda</h3>
            <p>Asigná un vigilador a un cliente con puntos de ronda configurados.</p>
            <div className="field"><label>Cliente *</label>
              <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, site_id: "" })} data-testid="patrol-client">
                <option value="">Elegir cliente...</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <SiteSelect clientId={form.client_id} value={form.site_id} onChange={(v) => setForm({ ...form, site_id: v })} testIdPrefix="patrol"/>
            <SiteAssignedHint clientId={form.client_id} siteId={form.site_id} roster={vigiladores}/>
            <div className="field"><label>Vigilador *</label>
              <select required value={form.vigilador_id} onChange={(e) => setForm({ ...form, vigilador_id: e.target.value })} data-testid="patrol-vigilador">
                <option value="">Elegir vigilador...</option>
                {vigiladores.map((v) => <option key={v.id} value={v.id}>{v.name} · {roleLabel(v.role)}</option>)}
              </select>
              {vigiladores.length === 0 && <div style={{ fontSize: 11, color: "#BF3437", marginTop: 4 }}>No hay vigiladores activos. Creá uno en Usuarios.</div>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div className="field"><label>Fecha programada *</label>
                <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="patrol-schedule-date"/>
              </div>
              <div className="field"><label>Hora (24 hs) *</label>
                <Time24Select required value={form.time} onChange={(v) => setForm({ ...form, time: v })} testId="patrol-schedule-time" />
              </div>
            </div>
            <div className="field"><label>Tolerancia horaria (minutos)</label>
              <input type="number" min="15" max="240" value={form.tolerance_minutes} onChange={(e) => setForm({ ...form, tolerance_minutes: e.target.value })}/>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="patrol-submit">{busy ? "Creando..." : "Crear ronda"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
